// src/components/MainPane.js
import React, { useEffect, useRef, useState } from 'react';
import RandomLoadingContent from './RandomLoadingContent.js';
import { Button, Box, useMediaQuery, useTheme } from '@mui/material';
import { keyframes } from '@emotion/react';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import MouseOutlinedIcon from '@mui/icons-material/MouseOutlined';
import { useRealtime } from '../contexts/RealtimeContext.js';
import { useParams }   from 'react-router-dom';

const subtlePulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50%      { transform: scale(1.1); opacity: 0.8; }
`;

const colorChange = keyframes`
  0%   { background: #0000FF; }
  100% { background: #BB0BC6; }
`;







export default function MainPane({
  viewMode,
  lastHtml,
  lastCss,
  lastJs,
  canvasContent,
  loading,
  iframeRef,
  comments,
  commentInput,
  onCommentSubmit,
  onIframeLoad,
  prototypeId,
  myId,
  isController,
  controllerId,
  collabSessionActive
}) {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));  // <600px
  const [tab, setTab] = useState(0);
  const mainPaneRef = useRef(null); // ⬅️ Create a ref
  const socket = useRealtime();
  const [cursors, setCursors] = useState({});
  const suppressNextEmit = useRef(false);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    function injectHandler() {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        if (!doc.body) return;

        // only inject once per load
        const SCRIPT_ID = 'collab-relay-script';
        // remove any old copy so we always get the fresh combo
        const old = doc.getElementById(SCRIPT_ID);
        if (old) old.remove();

        const script = doc.createElement('script');
        script.id = SCRIPT_ID;
        script.textContent = `
          (function(){
            let lastSent = document.documentElement.outerHTML;

            const throttle = (fn, wait) => {
              let last = 0;
              return (...args) => {
                if (Date.now() - last > wait) {
                  fn(...args);
                  last = Date.now();
                }
              };
            };

            // send cursor
            const sendCursor = throttle(e => {
              const x = e.clientX / window.innerWidth;
              const y = e.clientY / window.innerHeight;
              window.parent.postMessage({ type:'cursor_move', x, y }, '*');
            }, 16);
            document.addEventListener('mousemove', sendCursor);
            document.addEventListener('mouseleave', () =>
              window.parent.postMessage({ type:'cursor_leave' }, '*')
            );

            // send full HTML on any DOM change
            const sendContent = throttle(() => {
            const html = document.documentElement.outerHTML;
            if (html === lastSent) return;        // ← skip duplicates
            lastSent = html;
            window.parent.postMessage({ type:'content_update', html }, '*');
          }, 200);

          const obs = new MutationObserver(sendContent);
          obs.observe(document.body, {
            subtree: true,
            childList: true,
            attributes: true,
            characterData: true,
          });
        })();
        `;
        doc.head.appendChild(script);
      } catch (err) {
        // cross‐origin or other issues
        console.warn('Could not inject cursor relay:', err);
      }
    }

    iframe.addEventListener('load', injectHandler);
    // if it’s already loaded:
    injectHandler();

    return () => {
      iframe.removeEventListener('load', injectHandler);
    };
  }, [iframeRef, canvasContent,isController]);  // re-run whenever the preview HTML changes

  useEffect(() => {
    function handleCursorMove(data) {
      if (!collabSessionActive) return;
      setCursors(cs => ({ ...cs, [data.userId]: data }));
    }

    function handleCursorLeave({ userId }) {
      if (!collabSessionActive) return;
      setCursors(cs => {
        const next = { ...cs };
        delete next[userId];
        return next;
      });
    }

    socket.on('cursor_move', handleCursorMove);
    socket.on('cursor_leave', handleCursorLeave);
    return () => {
      socket.off('cursor_move', handleCursorMove);
      socket.off('cursor_leave', handleCursorLeave);
    };
  }, [socket]);


  // join exactly one room, based on the URL
  const { username, chatId } = useParams();
  const sessionRoom = `${username}/${chatId}`;
  useEffect(() => {
    if (!collabSessionActive) return;
    console.log('[MainPane] joining session-room', sessionRoom);
    socket.emit('join', { room: sessionRoom });
  }, [socket, sessionRoom, collabSessionActive]);

  useEffect(() => {
    // as soon as we’re in a live session *and* we’re the controller,
    // send the current canvasContent to the room
    if (collabSessionActive && isController && canvasContent) {
      console.log(`🏁 [client] controller broadcasting initial snapshot (length ${canvasContent.length})`)
      socket.emit('canvas_update', {
        room:     sessionRoom,
        content: canvasContent,
        clientId: socket.id
      })
    }
  }, [socket, prototypeId, collabSessionActive, isController, canvasContent])


  // track that we’ve actually started a load cycle
  const [hasTriedLoad, setHasTriedLoad] = useState(false);

  useEffect(() => {
    if (loading) setHasTriedLoad(true);
  }, [loading]);

  // state for forcing RandomLoadingContent to remount
  const [shuffleKey, setShuffleKey] = useState(0);

  useEffect(() => {
    function onMessage(e) {
      const d = e.data;
      if (!d?.type) return;

      switch (d.type) {
        case 'cursor_move':
          if (collabSessionActive) {
            socket.emit('cursor_move', {
              room: sessionRoom,
              userId: myId,
              x: d.x,
              y: d.y
            });
          }
          break;

        case 'cursor_leave':
          if (collabSessionActive) {
            socket.emit('cursor_leave', {
              room: sessionRoom,
              userId: myId
            });
          }
          break;

        case 'content_update':
          // only controller in an active session broadcasts DOM changes
          if (collabSessionActive && isController) {
            console.log('🏭 host got content_update', d.html.slice(0,50));
            socket.emit('canvas_update', {
              room: sessionRoom,
              content: d.html,
              clientId: socket.id
            });
          }
          break;
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [socket, prototypeId, myId, isController, collabSessionActive]);

  return (
    <div className="main-pane" ref={mainPaneRef}>
      {viewMode === 'code' ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 2, pb: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const combo = `
    <!-- HTML -->
    ${lastHtml}

    <!-- CSS -->
    <style>
    ${lastCss}
    </style>

    <!-- JS -->
    <script>
    ${lastJs}
    </script>`.trim();
                navigator.clipboard.writeText(combo);
              }}
            >
              Copy All Code
            </Button>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              px: 2,
              overflowX: 'hidden',
              flexWrap: 'nowrap',
            }}
          >
            {['HTML', 'CSS', 'JS'].map((label, i) => (
              <div key={label} style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ fontFamily: 'monospace', marginBottom: 4 }}>{label}</h3>
                <pre
                  className={`language-${label.toLowerCase()}`}
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    padding: '10px',
                    borderRadius: '8px',
                    backgroundColor: '#f5f5f5',
                    overflowX: 'auto',
                    fontSize: '13px',
                    height: '100%',
                    maxHeight: 'calc(100vh - 160px)',
                  }}
                >
                  <code>{[lastHtml, lastCss, lastJs][i].trim()}</code>
                </pre>
              </div>
            ))}
          </Box>
        </>
      ) : (
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            // 👉 allow interaction if NOT in a session (solo mode) OR if you’re the controller
            pointerEvents: (!collabSessionActive || isController) ? 'auto' : 'none',
          }}
        >
          {/* only show error when not loading & no content */}
          {hasTriedLoad && !loading && !canvasContent && (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#aaa',
                fontSize: 18,
                fontStyle: 'italic',
                textAlign: 'center',
                gap: 12,
              }}
            >
              <div style={{ fontWeight: 400, fontSize: 32 }}>
                Hmm…something isn’t right.
              </div>
            </div>
          )}

          {/* render iframe when we have content */}
          {canvasContent && (
            <iframe
              key={`session=${collabSessionActive};ctrl=${controllerId}::${canvasContent.slice(0,20)}`}
              srcDoc={canvasContent}
              // 🎯 if *not* in a collab-session, drop sandbox entirely;
              //    otherwise controller gets full scripts, viewers only modals
              {...(
                collabSessionActive
                  ? {
                      sandbox: isController
                        ? 'allow-scripts allow-modals allow-same-origin'
                        : 'allow-modals'
                    }
                  : {}
              )}
              ref={iframeRef}
              onLoad={onIframeLoad}
              style={{
                width: '100%',
                flex: 1,
                border: 'none',
                opacity: loading ? 0.3 : 1,
                transition: 'opacity 0.5s ease',
              }}
            />
          )}

          {/* Cursor overlays */}
          {Object.entries(cursors).map(([uid, { x, y, color }]) => (
            <MouseOutlinedIcon
              key={uid}
              style={{
                position: 'absolute',
                left: `${x * 100}%`,
                top: `${y * 100}%`,
                transform: 'translate(-50%, -50%)',
                color,
                fontSize: 24,
                pointerEvents: 'none',
                zIndex: 9999,
              }}
            />
          ))}

          {/* Loading overlay */}
          {loading && (
            <Box className="loading-box">
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    fontSize: 18,
                    color: '#1226eb',
                    fontWeight: 600,
                    animation: `${subtlePulse} 3s infinite ease-in-out`,
                  }}
                >
                  Generating
                </Box>
                <Box
                  sx={{
                    ml: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    paddingLeft: '10px',
                  }}
                >
                  {[0, 1, 2, 3].map(i => (
                    <Box
                      key={i}
                      sx={{
                        width: 15,
                        height: 15,
                        borderRadius: '50%',
                        backgroundColor: '#0000FF',
                        animation: `${colorChange} 2s infinite ease-in-out ${i * 0.2}s`,
                        margin: '0 5px',
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  startIcon={<ShuffleIcon />}
                  onClick={() => setShuffleKey(k => k + 1)}
                  sx={{
                    background:
                      'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                    color: 'text.primary',
                    borderRadius: 16,
                    px: 3,
                    py: 1,
                    fontWeight: 500,
                    boxShadow: '0px 1px 3px rgba(0,0,0,0.1)',
                    transition: 'transform 75ms ease-out, box-shadow 75ms ease-out',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: '0px 3px 8px rgba(0,0,0,0.15)',
                    },
                    '&:active': {
                      transform: 'scale(1.0)',
                      boxShadow: '0px 2px 4px rgba(0,0,0,0.12)',
                      transition: 'transform 50ms ease-out',
                    },
                  }}
                >
                  Shuffle
                </Button>
              </Box>
              <RandomLoadingContent key={shuffleKey} />
            </Box>
          )}

          {/* Comment bubbles */}
          {viewMode === 'comment' &&
            comments.map(c => (
              <div
                key={c.id}
                title={c.text}
                style={{
                  position: 'absolute',
                  top: c.y,
                  left: c.x,
                  transform: 'translate(-50%,-50%)',
                  background: '#222',
                  color: '#fff',
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  cursor: 'default',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                  zIndex: 5,
                }}
              >
                {c.id}
              </div>
            ))}

          {/* Comment input */}
          {commentInput && viewMode === 'comment' && (
            <div
              style={{
                position: 'absolute',
                top: commentInput.y,
                left: commentInput.x,
                background: '#fff',
                border: '1px solid #ccc',
                padding: 8,
                borderRadius: 6,
                width: 220,
                zIndex: 10,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            >
              <textarea
                value={commentInput.text}
                onChange={e => onCommentSubmit({ ...commentInput, text: e.target.value })}
                placeholder="Leave a comment…"
                style={{
                  width: '100%',
                  height: 60,
                  fontSize: 13,
                  borderRadius: 4,
                  border: '1px solid #ddd',
                  padding: 6,
                  resize: 'none',
                }}
              />
              <button
                onClick={onCommentSubmit}
                style={{
                  marginTop: 6,
                  padding: '6px 10px',
                  fontSize: 12,
                  background: '#111',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  float: 'right',
                }}
              >
                Submit
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

}





function FireflyBackground() {
  return (
    <div className="firefly-container">
      {[...Array(15)].map((_, i) => {
        const size = Math.floor(Math.random() * 8) + 4;
        const left = Math.random() * 100;
        const delay = Math.random() * 5;
        const duration = Math.random() * 10 + 8;
        return (
          <span key={i} className="firefly" style={{ left: `${left}%`, animationDelay: `${delay}s`, animationDuration: `${duration}s`, width: `${size}px`, height: `${size}px` }} />
        );
      })}
    </div>
  );
  
}
